import {useCallback, useEffect, useState} from 'react'
import {parseDiffHash} from './document-hash-helpers'
import {ssrSafeLocation} from '@github-ui/ssr-utils'

type UseSelectedDiffHash = {
  /** The currently selected path digest (without 'diff-' prefix) */
  selectedPathDigest: string
  /** The full diff hash with prefix if available */
  selectedFullDiffHash: string
  /** Function to set the selected hash */
  setSelectedHash: (hash: string) => void
}

/**
 * Hook that tracks the currently selected diff hash from the URL.
 *
 * This provides a consistent way to track diff navigation across different components
 * that need to respond to hash changes (like DiffFileTree, use-diff-navigation, etc.).
 *
 * @returns Object containing the selected diff hash information
 */
export function useSelectedDiffHash(): UseSelectedDiffHash {
  const [selectedHash, setSelectedHash] = useState<string>('')

  const onHashChange = useCallback(() => {
    const windowHash = parseDiffHash(ssrSafeLocation.hash ?? '') ?? ''
    const hashWithoutPrefix = windowHash.replace('diff-', '')
    setSelectedHash(hashWithoutPrefix)
  }, [])

  useEffect(() => {
    window.addEventListener('hashchange', onHashChange)
    onHashChange() // Initialize on mount
    return () => {
      window.removeEventListener('hashchange', onHashChange)
    }
  }, [onHashChange])

  return {
    selectedPathDigest: selectedHash,
    selectedFullDiffHash: selectedHash ? `diff-${selectedHash}` : '',
    setSelectedHash,
  }
}
